import type { NoteTemplate, SessionRating } from '@repo/shared-types';
import type { OrphanItem } from './NoteItemTable';

/** One tier's draft. `items`/`homeworkItems` are keyed by template item id
 *  (THI-193); `comment`/`homework` stay the free text (with a template they
 *  are the "Ghi chú ngoài sườn" / free homework). */
export type NoteDraft = {
  comment: string;
  homework: string;
  items: Record<string, string>;
  homeworkItems: Record<string, string>;
  /** THI-196: photos on this tier's homework group, in strip order. */
  photos: DraftPhoto[];
  /** THI-346: the personal tier's level, null when unrated. The class
   *  tier's draft carries null and never sends it. */
  rating: SessionRating | null;
};

/** A photo as the form holds it. `previewUrl` is an object URL for a
 *  just-picked file or the signed url of a stored photo; `saved` marks the
 *  ones that came back from the server (so the presign's `pendingCount`
 *  counts only what this edit added). */
export type DraftPhoto = {
  id: string;
  key: string;
  width: number;
  height: number;
  bytes: number;
  status: 'uploading' | 'ready' | 'failed';
  progress: number;
  previewUrl: string;
  saved: boolean;
};

export const photoCount = (d: NoteDraft | undefined): number =>
  (d?.photos ?? []).filter((p) => p.status !== 'failed').length;
export const hasAnyPhotos = (d: NoteDraft | undefined): boolean => photoCount(d) > 0;
export const anyUploading = (d: NoteDraft | undefined): boolean =>
  (d?.photos ?? []).some((p) => p.status === 'uploading');

/** What the dirty compare looks at: text, items, and photos by id + key
 *  only. A progress tick or a preview URL must never read as an edit. */
export function draftSignature(d: NoteDraft | undefined): string {
  if (!d) return '';
  return JSON.stringify({
    c: d.comment, h: d.homework, i: d.items, hi: d.homeworkItems,
    p: d.photos.filter((p) => p.status !== 'failed').map((p) => [p.id, p.key]),
    r: d.rating,
  });
}

export const hasAnyText = (d: NoteDraft | undefined): boolean =>
  Boolean(d && (d.comment.trim() || Object.values(d.items).some((v) => v.trim())));
export const hasAnyHomework = (d: NoteDraft | undefined): boolean =>
  Boolean(d && (d.homework.trim() || Object.values(d.homeworkItems).some((v) => v.trim())));

/** Preview under the student name: items with text in template order as
 *  "Tên mục: nội dung" · … then the free text; one line, CSS truncates. */
export function previewParts(d: NoteDraft | undefined, template: NoteTemplate | null, orphans: OrphanItem[]): { name: string | null; text: string }[] {
  if (!d) return [];
  const out: { name: string | null; text: string }[] = [];
  for (const it of template?.studentItems ?? []) {
    const v = (d.items[it.id] ?? '').trim();
    if (v) out.push({ name: it.name, text: v });
  }
  for (const o of orphans) {
    const v = (d.items[o.itemId] ?? '').trim();
    if (v) out.push({ name: o.name, text: v });
  }
  const free = d.comment.trim().split('\n')[0] ?? '';
  if (free) out.push({ name: null, text: free });
  return out;
}
